import { supabase } from "../admin/supabase";
import { getConsent, CONSENT_EVENT } from "../lib/cookieConsent";
import { tagEvent, tagEnquiryConversion, identifyVisitor } from "../lib/marketingTags";


/**
 * Lightweight, first-party visitor tracking.
 *
 * Records intent signals that matter commercially — programme card clicks,
 * tuition fee interactions and enquiry-form activity — into the
 * `visitor_events` table. Everything is best-effort and never blocks or
 * breaks the user journey, and nothing is recorded unless the visitor has
 * accepted analytics cookies.
 */

export const VISITOR_EVENT_TYPES = {
  PAGE_VIEW: "page_view",
  PROGRAMME_CARD_CLICK: "programme_card_click",
  TUITION_FEE_CLICK: "tuition_fee_click",
  ENQUIRY_FORM_START: "enquiry_form_start",
  ENQUIRY_FORM_SUBMIT: "enquiry_form_submit",
  CTA_CLICK: "cta_click",
  APPLICATION_CLICK: "application_click",
};

const DEVICE_KEY = "zu-visitor-device-id";
const SESSION_KEY = "zu-visitor-session-id";

function randomId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `v-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function readId(storage, key) {
  try {
    const existing = storage.getItem(key);
    if (existing) return existing;
    const created = randomId();
    storage.setItem(key, created);
    return created;
  } catch {
    return null;
  }
}

export function getVisitorDeviceId() {
  if (typeof window === "undefined") return null;
  return readId(window.localStorage, DEVICE_KEY);
}

export function getVisitorSessionId() {
  if (typeof window === "undefined") return null;
  return readId(window.sessionStorage, SESSION_KEY);
}

function analyticsAllowed() {
  const consent = getConsent();
  return !!(consent && consent.analytics);
}

/**
 * Coarse device class, derived from the viewport first (reliable on every
 * browser) and the user-agent as a fallback. Used so the dashboard can show
 * how much traffic is actually coming from phones.
 */
export function getDeviceType() {
  if (typeof window === "undefined") return "unknown";
  const ua = (navigator.userAgent || "").toLowerCase();
  const width = window.innerWidth || document.documentElement.clientWidth || 0;
  const uaMobile = /iphone|ipod|android.*mobile|windows phone|blackberry|opera mini/.test(ua);
  const uaTablet = /ipad|android(?!.*mobile)|tablet|kindle|silk/.test(ua);
  const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;

  if (uaMobile || (coarse && width > 0 && width < 768)) return "mobile";
  if (uaTablet || (coarse && width < 1024)) return "tablet";
  if (width && width < 768) return "mobile";
  if (width && width < 1024) return "tablet";
  return "desktop";
}

function truncate(value, max) {
  if (value === null || value === undefined) return null;
  const text = String(value);
  return text.length > max ? text.slice(0, max) : text;
}


// Events fired before the visitor answers the cookie banner are held here and
// sent as soon as analytics consent is granted, so the very first page view of
// a visit is never lost.
const pendingEvents = [];
const MAX_PENDING = 20;
let consentListenerBound = false;

function bindConsentListener() {
  if (consentListenerBound || typeof window === "undefined") return;
  consentListenerBound = true;
  window.addEventListener(CONSENT_EVENT, () => {
    if (!analyticsAllowed()) {
      pendingEvents.length = 0;
      return;
    }
    const queued = pendingEvents.splice(0, pendingEvents.length);
    queued.forEach((payload) => sendEvent(payload));
  });
}

async function sendEvent(payload) {
  try {
    const { error } = await supabase.from("visitor_events").insert(payload);
    if (error) {
      console.debug("Visitor event not recorded", error.message);
      return false;
    }
    return true;
  } catch (err) {
    console.debug("Visitor event not recorded", err?.message);
    return false;
  }
}

/**
 * Records a single visitor event. Returns true when it was sent.
 * Fire-and-forget: callers never need to await it.
 */
export async function trackVisitorEvent(eventType, options = {}) {
  if (typeof window === "undefined") return false;
  if (!Object.values(VISITOR_EVENT_TYPES).includes(eventType)) return false;

  const payload = {
    event_type: eventType,
    label: truncate(options.label, 250),
    target_path: truncate(options.targetPath, 600),
    page_path: truncate(options.pagePath || window.location.pathname + window.location.search, 600),
    referrer: truncate(options.referrer ?? (document.referrer || null), 600),
    device_id: getVisitorDeviceId(),
    session_id: getVisitorSessionId(),
    metadata: {
      ...(options.metadata && typeof options.metadata === "object" ? options.metadata : {}),
      device_type: getDeviceType(),
      viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    },
  };

  if (!analyticsAllowed()) {
    bindConsentListener();
    if (pendingEvents.length < MAX_PENDING) pendingEvents.push(payload);
    return false;
  }

  // Mirror the same signal to GA4 / Tag Manager / PostHog / ad pixels so every
  // reporting tool tells the same story. Page views are sent by PageViewTracker.
  try {
    identifyVisitor(payload.device_id, { device_type: payload.metadata.device_type });
    if (eventType !== VISITOR_EVENT_TYPES.PAGE_VIEW) {
      tagEvent(eventType, {
        label: payload.label,
        page_path: payload.page_path,
        target_path: payload.target_path,
        device_type: payload.metadata.device_type,
      });
    }
    if (eventType === VISITOR_EVENT_TYPES.ENQUIRY_FORM_SUBMIT) {
      tagEnquiryConversion({ label: payload.label, page_path: payload.page_path });
    }
  } catch {
    /* measurement must never break the page */
  }

  return sendEvent(payload);
}


/** One page view per route change (deduped against rapid re-renders). */
let lastPageViewKey = null;
export function trackPageView(pagePath, metadata = {}) {
  if (typeof window === "undefined") return false;
  const path = pagePath || window.location.pathname + window.location.search;
  const key = `${getVisitorSessionId() || ""}|${path}|${Math.floor(Date.now() / 1000)}`;
  if (key === lastPageViewKey) return false;
  lastPageViewKey = key;
  return trackVisitorEvent(VISITOR_EVENT_TYPES.PAGE_VIEW, {
    pagePath: path,
    label: truncate(document.title, 250),
    metadata,
  });
}


export const trackProgrammeCardClick = (label, targetPath, metadata) =>
  trackVisitorEvent(VISITOR_EVENT_TYPES.PROGRAMME_CARD_CLICK, { label, targetPath, metadata });

export const trackTuitionFeeClick = (label, metadata) =>
  trackVisitorEvent(VISITOR_EVENT_TYPES.TUITION_FEE_CLICK, { label, metadata });

export const trackEnquiryFormStart = (label, metadata) =>
  trackVisitorEvent(VISITOR_EVENT_TYPES.ENQUIRY_FORM_START, { label, metadata });

export const trackEnquiryFormSubmit = (label, metadata) =>
  trackVisitorEvent(VISITOR_EVENT_TYPES.ENQUIRY_FORM_SUBMIT, { label, metadata });

// ------------------------------------------------------------------
// Application funnel — clicks through to the Sajili application portal
// ------------------------------------------------------------------

/** Hosts that mean "the visitor has gone off to actually apply". */
export const APPLICATION_HOSTS = ["sajili.zetech.ac.ke"];

export function isApplicationUrl(url) {
  if (!url) return false;
  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.href : "https://www.zetech.ac.ke");
    return APPLICATION_HOSTS.some((host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`));
  } catch {
    return false;
  }
}

export const trackApplicationClick = (label, targetPath, metadata) =>
  trackVisitorEvent(VISITOR_EVENT_TYPES.APPLICATION_CLICK, { label, targetPath, metadata });

/**
 * Site-wide capture of every "Apply Now" click, wherever it lives — hero
 * buttons, navbar, floats, popups, footers, programme pages. One delegated
 * listener means new apply links are tracked automatically, with no need to
 * remember to instrument them.
 */
export function initApplicationClickTracking() {
  if (typeof document === "undefined") return () => {};

  const handler = (event) => {
    const anchor = event.target && event.target.closest ? event.target.closest("a[href]") : null;
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!isApplicationUrl(href)) return;

    const label =
      (anchor.textContent || "").trim().slice(0, 120) ||
      anchor.getAttribute("aria-label") ||
      "Apply Now";

    trackApplicationClick(label, href, {
      section: anchor.closest("[data-track-section]")?.getAttribute("data-track-section") || null,
      link_id: anchor.id || null,
    });
  };

  document.addEventListener("click", handler, true);
  return () => document.removeEventListener("click", handler, true);
}

// ------------------------------------------------------------------
// Admin reporting
// ------------------------------------------------------------------

const MAX_ROWS = 20000;
const PAGE_SIZE = 1000;

const EVENT_COLUMNS =
  "id, event_type, label, target_path, page_path, referrer, device_id, session_id, metadata, created_at";


/**
 * Raw events within the last `days` days (admin only, RLS enforced).
 * Reads in pages so busy periods are never silently truncated at 1000 rows.
 */
export async function getVisitorEvents({ days = 30, limit = MAX_ROWS, since: explicitSince } = {}) {
  const since = explicitSince || new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const rows = [];

  for (let offset = 0; offset < limit; offset += PAGE_SIZE) {
    const upper = Math.min(offset + PAGE_SIZE, limit) - 1;
    const { data, error } = await supabase
      .from("visitor_events")
      .select(EVENT_COLUMNS)
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .range(offset, upper);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < upper - offset + 1) break;
  }

  return rows;
}

/** Exact count used to prove a browser-side reporting fallback is complete. */
export async function getVisitorEventCount({ days = 30, since: explicitSince } = {}) {
  const since = explicitSince || new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const { count, error } = await supabase
    .from("visitor_events")
    .select("id", { count: "exact", head: true })
    .gte("created_at", since);
  if (error) throw error;
  return count || 0;
}

/**
 * Actual enquiries saved to the database in the period. This is the source of
 * truth for "enquiries submitted" — click tracking only fires for visitors who
 * accepted analytics cookies, so it always under-counts real enquiries.
 */
export async function getEnquiryCount({ days = 30 } = {}) {
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const { count, error } = await supabase
    .from("admissions_enquiries")
    .select("id", { count: "exact", head: true })
    .gte("created_at", since);
  if (error) throw error;
  return count || 0;
}

function countBy(rows, key) {
  const counts = {};
  rows.forEach((row) => {
    const value = row[key] || "—";
    counts[value] = (counts[value] || 0) + 1;
  });
  return counts;
}

function toSortedList(counts, limit = 10) {
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));
}

// --- Channel grouping (mirrors GA4's default channel groups) -------------

const SEARCH_HOSTS = /google\.|bing\.|yahoo\.|duckduckgo\.|search\.brave|ecosia\.|yandex\.|baidu\.|googlequicksearchbox/i;
const AI_HOSTS = /chatgpt\.com|chat\.openai|openai\.com|perplexity\.ai|claude\.ai|copilot\.microsoft|gemini\.google|bard\.google|you\.com|phind\.com/i;
const SOCIAL_HOSTS = /facebook\.|fb\.|instagram\.|twitter\.|t\.co|x\.com|linkedin\.|tiktok\.|youtube\.|youtu\.be|whatsapp\.|pinterest\.|telegram\./i;

/** Best-effort marketing channel for a single event row. */
export function classifyChannel(row = {}) {
  const page = String(row.page_path || "");
  const query = page.includes("?") ? page.slice(page.indexOf("?")).toLowerCase() : "";
  if (/gclid=|utm_medium=cpc|utm_medium=ppc|utm_medium=paid/.test(query)) return "Paid Search";
  if (/fbclid=|utm_medium=paid_social/.test(query)) return "Paid Social";
  if (/utm_medium=email/.test(query)) return "Email";
  if (/utm_medium=display|utm_medium=banner/.test(query)) return "Display";

  const ref = String(row.referrer || "").trim();
  if (!ref) return "Direct";
  if (AI_HOSTS.test(ref)) return "AI Assistant";
  if (SEARCH_HOSTS.test(ref)) return "Organic Search";
  if (SOCIAL_HOSTS.test(ref)) return "Organic Social";
  return "Referral";
}

/**
 * Average engagement time per visitor, GA4-style: for each session take the
 * span between its first and last event (capped at 30 minutes so an abandoned
 * tab never distorts the number), then average across visitors.
 */
function engagementMetrics(rows) {
  const bounds = {};
  rows.forEach((row) => {
    if (!row.session_id || !row.created_at) return;
    const time = new Date(row.created_at).getTime();
    if (Number.isNaN(time)) return;
    const current = bounds[row.session_id] || { min: time, max: time, device: row.device_id };
    current.min = Math.min(current.min, time);
    current.max = Math.max(current.max, time);
    bounds[row.session_id] = current;
  });

  const perSession = Object.values(bounds).map((b) => Math.min(b.max - b.min, 30 * 60 * 1000) / 1000);
  const totalSeconds = perSession.reduce((sum, n) => sum + n, 0);

  const perVisitor = {};
  Object.values(bounds).forEach((b) => {
    if (!b.device) return;
    perVisitor[b.device] = (perVisitor[b.device] || 0) + Math.min(b.max - b.min, 30 * 60 * 1000) / 1000;
  });
  const visitorValues = Object.values(perVisitor);

  return {
    avgSessionSeconds: perSession.length ? totalSeconds / perSession.length : 0,
    avgVisitorSeconds: visitorValues.length
      ? visitorValues.reduce((sum, n) => sum + n, 0) / visitorValues.length
      : 0,
  };
}

/** "1m 47s" style duration used by the dashboard. */
export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(seconds || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return mins ? `${mins}m ${secs}s` : `${secs}s`;
}


/** Everything the visitor analytics dashboard needs, derived in one pass. */
export function summariseVisitorEvents(rows = []) {
  const byType = countBy(rows, "event_type");
  const programmeClicks = rows.filter((r) => r.event_type === VISITOR_EVENT_TYPES.PROGRAMME_CARD_CLICK);
  const feeClicks = rows.filter((r) => r.event_type === VISITOR_EVENT_TYPES.TUITION_FEE_CLICK);
  const enquiryStarts = rows.filter((r) => r.event_type === VISITOR_EVENT_TYPES.ENQUIRY_FORM_START);
  const enquirySubmits = rows.filter((r) => r.event_type === VISITOR_EVENT_TYPES.ENQUIRY_FORM_SUBMIT);
  const pageViewRows = rows.filter((r) => r.event_type === VISITOR_EVENT_TYPES.PAGE_VIEW);
  const applicationClicks = rows.filter((r) => r.event_type === VISITOR_EVENT_TYPES.APPLICATION_CLICK);

  // Device split, taken from the device_type recorded with every event.
  const devices = {};
  const devicesByVisitor = {};
  rows.forEach((r) => {
    const type = (r.metadata && r.metadata.device_type) || "unknown";
    devices[type] = (devices[type] || 0) + 1;
    if (r.device_id) devicesByVisitor[r.device_id] = type;
  });
  const deviceVisitorValues = Object.values(devicesByVisitor);
  const mobileVisitors = deviceVisitorValues.filter((t) => t === "mobile").length;
  const mobileShare = deviceVisitorValues.length
    ? (mobileVisitors / deviceVisitorValues.length) * 100
    : 0;



  const uniqueVisitors = new Set(rows.map((r) => r.device_id).filter(Boolean)).size;
  const sessions = new Set(rows.map((r) => r.session_id).filter(Boolean)).size;

  // Sessions per device tells us how many people came back more than once.
  const sessionsByDevice = {};
  rows.forEach((r) => {
    if (!r.device_id || !r.session_id) return;
    (sessionsByDevice[r.device_id] = sessionsByDevice[r.device_id] || new Set()).add(r.session_id);
  });
  const returningVisitors = Object.values(sessionsByDevice).filter((set) => set.size > 1).length;
  const eventsPerSession = sessions ? rows.length / sessions : 0;
  const engagedSessions = (() => {
    const perSession = {};
    rows.forEach((r) => {
      if (!r.session_id) return;
      perSession[r.session_id] = (perSession[r.session_id] || 0) + 1;
    });
    return Object.values(perSession).filter((n) => n > 1).length;
  })();

  // Daily trend (oldest → newest) for the sparkline table.
  const daily = {};
  rows.forEach((row) => {
    const day = (row.created_at || "").slice(0, 10);
    if (!day) return;
    daily[day] = (daily[day] || 0) + 1;
  });
  const trend = Object.entries(daily)
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([day, count]) => ({ day, count }));

  const referrers = countBy(
    rows.filter((r) => r.referrer && !String(r.referrer).includes("zetech.ac.ke")),
    "referrer"
  );

  const first = rows.length ? rows[rows.length - 1].created_at : null;
  const last = rows.length ? rows[0].created_at : null;

  // --- GA-style acquisition + engagement -------------------------------
  const engagement = engagementMetrics(rows);

  // New visitors = devices whose first ever event in this window is also the
  // start of their only session (best-effort "new users" equivalent).
  const newVisitors = Object.values(sessionsByDevice).filter((set) => set.size === 1).length;

  // Channel is decided once per session, from that session's earliest event —
  // exactly how GA groups "sessions by default channel group".
  const sessionFirstRow = {};
  rows.forEach((row) => {
    if (!row.session_id) return;
    const existing = sessionFirstRow[row.session_id];
    if (!existing || new Date(row.created_at) < new Date(existing.created_at)) {
      sessionFirstRow[row.session_id] = row;
    }
  });
  const channelCounts = {};
  const visitorsByChannel = {};
  Object.values(sessionFirstRow).forEach((row) => {
    const channel = classifyChannel(row);
    channelCounts[channel] = (channelCounts[channel] || 0) + 1;
    (visitorsByChannel[channel] = visitorsByChannel[channel] || new Set()).add(row.device_id);
  });
  const channels = Object.entries(channelCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({
      label,
      count,
      visitors: visitorsByChannel[label] ? visitorsByChannel[label].size : 0,
    }));

  // Views by page title, the way GA's "Views by page title" card reads.
  const titleRows = pageViewRows.filter((r) => r.label);
  const topPageTitles = toSortedList(countBy(titleRows, "label"), 10);

  // Traffic by hour of day (local browser time of the reader).
  const hourly = Array.from({ length: 24 }, (_, hour) => ({ hour, count: 0 }));
  rows.forEach((row) => {
    const date = new Date(row.created_at);
    if (!Number.isNaN(date.getTime())) hourly[date.getHours()].count += 1;
  });

  return {

    totalEvents: rows.length,
    pageViews: byType[VISITOR_EVENT_TYPES.PAGE_VIEW] || 0,
    ctaClicks: byType[VISITOR_EVENT_TYPES.CTA_CLICK] || 0,
    returningVisitors,
    engagedSessions,
    engagementRate: sessions ? (engagedSessions / sessions) * 100 : 0,
    eventsPerSession,
    firstEventAt: first,
    lastEventAt: last,
    uniqueVisitors,
    sessions,
    byType,
    programmeClicks: programmeClicks.length,
    feeClicks: feeClicks.length,
    enquiryStarts: enquiryStarts.length,
    enquirySubmits: enquirySubmits.length,
    enquiryConversion: enquiryStarts.length
      ? (enquirySubmits.length / enquiryStarts.length) * 100
      : 0,
    topProgrammes: toSortedList(countBy(programmeClicks, "label")),
    topFeeInteractions: toSortedList(countBy(feeClicks, "label")),
    topPages: toSortedList(countBy(pageViewRows.length ? pageViewRows : rows, "page_path")),
    topReferrers: toSortedList(referrers, 8),
    devices,
    mobileShare,
    mobileVisitors,
    topDevices: toSortedList(devices, 5),
    trend,
    newVisitors,
    channels,
    topPageTitles,
    hourly,
    avgSessionSeconds: engagement.avgSessionSeconds,
    avgVisitorSeconds: engagement.avgVisitorSeconds,

    // --- Homepage → application funnel --------------------------------
    applicationClicks: applicationClicks.length,
    applicants: new Set(applicationClicks.map((r) => r.device_id).filter(Boolean)).size,
    applicationRate: uniqueVisitors
      ? (new Set(applicationClicks.map((r) => r.device_id).filter(Boolean)).size / uniqueVisitors) * 100
      : 0,
    topApplicationSources: toSortedList(countBy(applicationClicks, "page_path"), 10),
    topApplicationButtons: toSortedList(countBy(applicationClicks, "label"), 10),
    funnel: (() => {
      const visitorsWith = (list) => new Set(list.map((r) => r.device_id).filter(Boolean)).size;
      const programmeVisitors = visitorsWith(programmeClicks);
      const feeVisitors = visitorsWith([...programmeClicks, ...feeClicks]);
      const applyVisitors = visitorsWith(applicationClicks);
      return [
        { stage: "Visited the site", visitors: uniqueVisitors },
        { stage: "Explored a programme", visitors: programmeVisitors },
        { stage: "Checked programmes or fees", visitors: feeVisitors },
        { stage: "Enquired", visitors: visitorsWith(enquirySubmits) },
        { stage: "Started an application (Sajili)", visitors: applyVisitors },
      ];
    })(),
  };
}


/**
 * Summary for an explicit [since, until) window, derived from the stored
 * events themselves so the numbers always match the detail tables.
 */
export async function getVisitorAnalyticsSummary({ since, until, limit = MAX_ROWS } = {}) {
  const from = since || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const rows = [];

  for (let offset = 0; offset < limit; offset += PAGE_SIZE) {
    const upper = Math.min(offset + PAGE_SIZE, limit) - 1;
    let query = supabase
      .from("visitor_events")
      .select(EVENT_COLUMNS)
      .gte("created_at", from);
    if (until) query = query.lt("created_at", until);
    const { data, error } = await query
      .order("created_at", { ascending: false })
      .range(offset, upper);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < upper - offset + 1) break;
  }

  return summariseVisitorEvents(rows);
}
